import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { visitsAPI, clientsAPI, caregiverProfilesAPI, usersAPI } from '../services/api';
import { utcIsoToClientTzInput, clientTzInputToUtcIso, tzAbbrev } from '../services/timezone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Clock,
  CheckCircle,
  Play,
  User,
  PencilSimple,
  Trash,
  XCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const SchedulePage = () => {
  const { user } = useAuth();
  const [visits, setVisits] = useState([]);
  const [clients, setClients] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [formData, setFormData] = useState({
    client_id: '',
    caregiver_id: '',
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    scheduled_time: '09:00',
    duration_minutes: 60,
    notes: ''
  });
  const [editingVisit, setEditingVisit] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    client_id: '', caregiver_id: '', scheduled_date: '', scheduled_time: '',
    duration_minutes: 60, notes: '', status: 'scheduled',
    check_in_time: '', check_out_time: '', change_reason: '',
    tasks_completed: [], billing_rate_override: 'auto'
  });
  const [tasksDirty, setTasksDirty] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [visitsRes, clientsRes, cgProfilesRes, supervisorsRes] = await Promise.all([
        visitsAPI.getAll({}),
        clientsAPI.getAll(),
        user?.role === 'supervisor' ? caregiverProfilesAPI.getAll() : Promise.resolve({ data: [] }),
        user?.role === 'supervisor' ? usersAPI.getAll('supervisor') : Promise.resolve({ data: [] })
      ]);
      setVisits(visitsRes.data);
      setClients(clientsRes.data);
      // Active caregivers from the Caregivers list (filters out test users
      // and inactive caregivers). Profiles without a login are kept but
      // rendered disabled with a "(needs login)" hint.
      const activeProfiles = (cgProfilesRes.data || [])
        .filter(p => (p.status || 'active') !== 'inactive'
                  && (p.employment_status || 'active') === 'active');
      const cgDropdown = activeProfiles.map(p => ({
        id: p.user_id && p.user_id !== 'none' ? p.user_id : `__nologin__:${p.id}`,
        name: p.name,
        needs_login: !p.user_id || p.user_id === 'none',
        is_supervisor: false,
        profile_id: p.id,
      }));
      // Supervisors who can double as caregivers on a visit. Exclude any
      // already represented as a caregiver above (by user_id) to avoid dupes.
      const cgUserIds = new Set(cgDropdown.filter(c => !c.needs_login).map(c => c.id));
      const supDropdown = (supervisorsRes.data || [])
        .filter(u => u.is_active !== false && !cgUserIds.has(u.id))
        .map(u => ({
          id: u.id, name: u.name, needs_login: false, is_supervisor: true,
        }));
      const merged = [...cgDropdown, ...supDropdown].sort((a, b) => a.name.localeCompare(b.name));
      setCaregivers(merged);
    } catch (error) {
      console.error('Error loading schedule:', error);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVisit = async (e) => {
    e.preventDefault();
    try {
      const payRate = formData.pay_rate_override_enabled && formData.pay_rate_override !== ''
        ? parseFloat(formData.pay_rate_override) : null;
      const bonus = formData.bonus_amount !== '' ? parseFloat(formData.bonus_amount) : null;
      await visitsAPI.create({
        client_id: formData.client_id,
        caregiver_id: formData.caregiver_id,
        scheduled_date: formData.scheduled_date,
        scheduled_time: formData.scheduled_time,
        duration_minutes: parseInt(formData.duration_minutes) || 60,
        notes: formData.notes || null,
        pay_rate_override: payRate,
        bonus_amount: bonus,
        bonus_reason: bonus ? (formData.bonus_reason || null) : null,
      });
      toast.success('Visit scheduled successfully');
      setIsCreateOpen(false);
      setFormData({
        client_id: '',
        caregiver_id: '',
        scheduled_date: format(new Date(), 'yyyy-MM-dd'),
        scheduled_time: '09:00',
        duration_minutes: 60,
        notes: '',
        pay_rate_override_enabled: false,
        pay_rate_override: '',
        bonus_amount: '',
        bonus_reason: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to schedule visit');
    }
  };

  const getGPS = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) { resolve({}); return; }
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const lat = pos.coords.latitude, lng = pos.coords.longitude;
          let address = `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
          try { const r = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18`); const d = await r.json(); if (d.display_name) address = d.display_name; } catch (e) {}
          resolve({ latitude: lat, longitude: lng, address });
        },
        () => resolve({}),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  const handleCheckIn = async (visitId) => {
    try {
      const gps = await getGPS();
      await visitsAPI.checkIn(visitId, gps);
      toast.success('Checked in successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to check in');
    }
  };

  const handleCheckOut = async (visitId) => {
    try {
      const gps = await getGPS();
      await visitsAPI.checkOut(visitId, gps);
      toast.success('Checked out successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to check out');
    }
  };

  const openEditVisit = (visit) => {
    setEditingVisit(visit);
    const tz = clients.find(c => c.id === visit.client_id)?.timezone || 'America/New_York';
    // Render stored UTC ISO into the client's local TZ (not the viewer's browser TZ)
    const cinLocal = visit.check_in_time ? utcIsoToClientTzInput(visit.check_in_time, tz) : '';
    const coutLocal = visit.check_out_time ? utcIsoToClientTzInput(visit.check_out_time, tz) : '';
    setEditForm({
      client_id: visit.client_id,
      caregiver_id: visit.caregiver_id,
      scheduled_date: visit.scheduled_date,
      scheduled_time: visit.scheduled_time,
      duration_minutes: visit.duration_minutes,
      notes: visit.notes || '',
      status: visit.status,
      check_in_time: cinLocal,
      check_out_time: coutLocal,
      change_reason: '',
      tasks_completed: Array.isArray(visit.tasks_completed) ? visit.tasks_completed.map(t => ({ ...t })) : [],
      billing_rate_override: visit.billing_rate_override || 'auto',
      pay_rate_override_enabled: visit.pay_rate_override != null,
      pay_rate_override: visit.pay_rate_override != null ? String(visit.pay_rate_override) : '',
      bonus_amount: visit.bonus_amount != null ? String(visit.bonus_amount) : '',
      bonus_reason: visit.bonus_reason || ''
    });
    setTasksDirty(false);
    setIsEditOpen(true);
  };

  const handleEditVisit = async (e) => {
    e.preventDefault();
    try {
      const newPayOverride = editForm.pay_rate_override_enabled && editForm.pay_rate_override !== ''
        ? parseFloat(editForm.pay_rate_override) : null;
      const newBonus = editForm.bonus_amount !== '' ? parseFloat(editForm.bonus_amount) : null;
      await visitsAPI.update(editingVisit.id, {
        client_id: editForm.client_id,
        caregiver_id: editForm.caregiver_id,
        scheduled_date: editForm.scheduled_date,
        scheduled_time: editForm.scheduled_time,
        duration_minutes: editForm.duration_minutes,
        notes: editForm.notes || null,
        pay_rate_override: newPayOverride,
        bonus_amount: newBonus,
        bonus_reason: newBonus ? (editForm.bonus_reason || null) : null,
      });
      // Update status if changed
      if (editForm.status !== editingVisit.status) {
        await visitsAPI.updateStatus(editingVisit.id, editForm.status);
      }
      // Update clock times and/or tasks_completed if changed (TZ-aware)
      const tz = clients.find(c => c.id === editForm.client_id)?.timezone || 'America/New_York';
      const origIn = editingVisit.check_in_time ? utcIsoToClientTzInput(editingVisit.check_in_time, tz) : '';
      const origOut = editingVisit.check_out_time ? utcIsoToClientTzInput(editingVisit.check_out_time, tz) : '';
      const clockTimesChanged = editForm.check_in_time !== origIn || editForm.check_out_time !== origOut;
      const origOverride = editingVisit.billing_rate_override || 'auto';
      const overrideChanged = editForm.billing_rate_override !== origOverride;
      const origPayOverride = editingVisit.pay_rate_override != null ? Number(editingVisit.pay_rate_override) : null;
      const payOverrideChanged = (origPayOverride ?? null) !== (newPayOverride ?? null);
      const origBonus = editingVisit.bonus_amount != null ? Number(editingVisit.bonus_amount) : null;
      const bonusChanged = (origBonus ?? null) !== (newBonus ?? null) ||
        (editForm.bonus_reason || '') !== (editingVisit.bonus_reason || '');
      if (clockTimesChanged || tasksDirty || overrideChanged || payOverrideChanged || bonusChanged) {
        if (!editForm.change_reason || !editForm.change_reason.trim()) {
          toast.error('Please provide a Reason for Changes before saving');
          return;
        }
        const payload = { change_reason: editForm.change_reason.trim() };
        if (clockTimesChanged) {
          payload.check_in_time = editForm.check_in_time ? clientTzInputToUtcIso(editForm.check_in_time, tz) : '';
          payload.check_out_time = editForm.check_out_time ? clientTzInputToUtcIso(editForm.check_out_time, tz) : '';
        }
        if (tasksDirty) {
          payload.tasks_completed = editForm.tasks_completed;
        }
        if (overrideChanged) {
          payload.billing_rate_override = editForm.billing_rate_override;
        }
        if (payOverrideChanged) {
          if (newPayOverride == null) {
            payload.clear_pay_rate_override = true;
          } else {
            payload.pay_rate_override = newPayOverride;
          }
        }
        if (bonusChanged) {
          payload.bonus_amount = newBonus != null ? newBonus : 0;
          payload.bonus_reason = newBonus ? (editForm.bonus_reason || '') : '';
        }
        await visitsAPI.updateTimes(editingVisit.id, payload);
      }
      toast.success('Visit updated');
      setIsEditOpen(false);
      setEditingVisit(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update visit');
    }
  };

  const toggleTaskCompleted = (idx) => {
    setEditForm(prev => ({
      ...prev,
      tasks_completed: prev.tasks_completed.map((t, i) =>
        i === idx ? { ...t, completed: !t.completed } : t
      )
    }));
    setTasksDirty(true);
  };

  const updateTaskNotes = (idx, notes) => {
    setEditForm(prev => ({
      ...prev,
      tasks_completed: prev.tasks_completed.map((t, i) =>
        i === idx ? { ...t, notes } : t
      )
    }));
    setTasksDirty(true);
  };

  const handleDeleteVisit = async () => {
    if (!editingVisit) return;
    setDeleting(true);
    try {
      await visitsAPI.delete(editingVisit.id);
      toast.success('Visit deleted');
      setIsDeleteOpen(false);
      setIsEditOpen(false);
      setEditingVisit(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete visit');
    } finally { setDeleting(false); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const filteredVisits = visits.filter(v => v.scheduled_date === selectedDateStr);

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Unknown Client';
  };

  const getCaregiverName = (caregiverId) => {
    const caregiver = caregivers.find(c => c.id === caregiverId);
    return caregiver?.name || 'Unknown Caregiver';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
            Schedule
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage visits and appointments
          </p>
        </div>
        {user?.role === 'supervisor' && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl gap-2" data-testid="schedule-visit-btn">
                <Plus weight="bold" className="w-4 h-4" />
                Schedule Visit
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Schedule New Visit</DialogTitle>
                <DialogDescription>
                  Create a new visit appointment
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateVisit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select value={formData.client_id} onValueChange={(value) => setFormData({...formData, client_id: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="visit-client-select">
                      <SelectValue placeholder="Select client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Caregiver *</Label>
                  <Select value={formData.caregiver_id} onValueChange={(value) => setFormData({...formData, caregiver_id: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="visit-caregiver-select">
                      <SelectValue placeholder="Select caregiver" />
                    </SelectTrigger>
                    <SelectContent>
                      {caregivers.map(cg => (
                        <SelectItem key={cg.id} value={cg.id} disabled={cg.needs_login} data-testid={`visit-cg-opt-${cg.profile_id || cg.id}`}>
                          {cg.name}
                          {cg.is_supervisor && (
                            <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-semibold">Supervisor</span>
                          )}
                          {cg.needs_login && (
                            <span className="ml-2 text-xs text-amber-600 font-medium">(needs login — add credentials on Caregivers page)</span>
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date *</Label>
                    <Input
                      type="date"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({...formData, scheduled_date: e.target.value})}
                      required
                      className="rounded-xl"
                      data-testid="visit-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Time *</Label>
                    <Input
                      type="time"
                      value={formData.scheduled_time}
                      onChange={(e) => setFormData({...formData, scheduled_time: e.target.value})}
                      required
                      className="rounded-xl"
                      data-testid="visit-time-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Select 
                    value={String(formData.duration_minutes)} 
                    onValueChange={(value) => setFormData({...formData, duration_minutes: parseInt(value)})}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="visit-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="180">3 hours</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                      <SelectItem value="300">5 hours</SelectItem>
                      <SelectItem value="360">6 hours</SelectItem>
                      <SelectItem value="420">7 hours</SelectItem>
                      <SelectItem value="480">8 hours</SelectItem>
                      <SelectItem value="540">9 hours</SelectItem>
                      <SelectItem value="600">10 hours</SelectItem>
                      <SelectItem value="660">11 hours</SelectItem>
                      <SelectItem value="720">12 hours</SelectItem>
                      <SelectItem value="780">13 hours</SelectItem>
                      <SelectItem value="840">14 hours</SelectItem>
                      <SelectItem value="900">15 hours</SelectItem>
                      <SelectItem value="960">16 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Special instructions or notes..."
                    className="rounded-xl"
                    data-testid="visit-notes-input"
                  />
                </div>

                {/* Caregiver Pay Override + Bonus (does not affect client billing) */}
                <div className="rounded-xl border border-border p-3 space-y-3 bg-muted/20">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Caregiver Pay (this visit only)</p>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded"
                      checked={formData.pay_rate_override_enabled}
                      onChange={(e) => setFormData({ ...formData, pay_rate_override_enabled: e.target.checked, pay_rate_override: e.target.checked ? formData.pay_rate_override : '' })}
                      data-testid="visit-pay-override-toggle"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Override caregiver hourly rate</p>
                      <p className="text-xs text-muted-foreground">Off = use caregiver's default rate. On = flat $/hr for this visit (no OT/weekend/holiday math).</p>
                    </div>
                  </label>
                  {formData.pay_rate_override_enabled && (
                    <div className="pl-7 space-y-1.5">
                      <Label className="text-xs">Custom rate ($/hr)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.pay_rate_override}
                          onChange={(e) => setFormData({ ...formData, pay_rate_override: e.target.value })}
                          placeholder="25.00"
                          className="rounded-xl pl-6"
                          data-testid="visit-pay-override-amount"
                        />
                      </div>
                    </div>
                  )}
                  <div className="border-t border-border pt-3 space-y-1.5">
                    <Label className="text-xs">Bonus (paid to caregiver, NOT billed to client)</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-2">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.bonus_amount}
                          onChange={(e) => setFormData({ ...formData, bonus_amount: e.target.value })}
                          placeholder="0.00"
                          className="rounded-xl pl-6"
                          data-testid="visit-bonus-amount"
                        />
                      </div>
                      <Input
                        value={formData.bonus_reason}
                        onChange={(e) => setFormData({ ...formData, bonus_reason: e.target.value })}
                        placeholder="Reason (e.g. covered short-notice shift)"
                        className="rounded-xl"
                        disabled={!formData.bonus_amount || parseFloat(formData.bonus_amount) <= 0}
                        data-testid="visit-bonus-reason"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 rounded-xl" data-testid="create-visit-submit">
                    Schedule Visit
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="rounded-2xl border border-border lg:col-span-1" data-testid="calendar-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CalendarIcon weight="duotone" className="w-5 h-5" />
              Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-xl"
              data-testid="schedule-calendar"
            />
          </CardContent>
        </Card>

        {/* Visits for Selected Date */}
        <Card className="rounded-2xl border border-border lg:col-span-2" data-testid="visits-list-card">
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              Visits for {format(selectedDate, 'MMMM d, yyyy')}
            </CardTitle>
            <CardDescription>
              {filteredVisits.length} visit{filteredVisits.length !== 1 ? 's' : ''} scheduled
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredVisits.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <CalendarIcon weight="duotone" className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No visits scheduled for this date</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredVisits.map((visit) => {
                  const isUnassigned = !visit.caregiver_id || visit.caregiver_id === '' || visit.caregiver_id === 'none';
                  const cgLabel = user?.role === 'caregiver'
                    ? 'You'
                    : (isUnassigned ? 'UNASSIGNED' : getCaregiverName(visit.caregiver_id));
                  return (
                  <div
                    key={visit.id}
                    className={`p-4 rounded-xl transition-colors ${
                      isUnassigned && user?.role !== 'caregiver'
                        ? 'bg-red-50 border-2 border-red-400 hover:bg-red-100'
                        : 'bg-muted/30 hover:bg-muted/50'
                    }`}
                    data-testid={`visit-card-${visit.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          isUnassigned && user?.role !== 'caregiver' ? 'bg-red-100' : 'bg-primary/10'
                        }`}>
                          <Clock weight="duotone" className={`w-6 h-6 ${
                            isUnassigned && user?.role !== 'caregiver' ? 'text-red-600' : 'text-primary'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-foreground">
                            {visit.scheduled_time} - {getClientName(visit.client_id)}
                          </p>
                          <p className={`text-sm flex items-center gap-1 mt-1 ${
                            isUnassigned && user?.role !== 'caregiver' ? 'text-red-700 font-medium' : 'text-muted-foreground'
                          }`}>
                            <User weight="duotone" className="w-4 h-4" />
                            {cgLabel}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Duration: {visit.duration_minutes} minutes
                          </p>
                          {visit.notes && (
                            <p className="text-sm text-muted-foreground mt-2 italic">
                              "{visit.notes}"
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={`${getStatusColor(visit.status)} rounded-full`}>
                          {visit.status.replace('_', ' ')}
                        </Badge>
                        <div className="flex gap-2">
                          {(user?.role === 'supervisor' || user?.role === 'super_admin') && (
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="rounded-xl gap-1"
                              onClick={() => openEditVisit(visit)}
                              data-testid={`edit-visit-btn-${visit.id}`}
                            >
                              <PencilSimple weight="bold" className="w-3 h-3" />
                              Edit
                            </Button>
                          )}
                          {(user?.role === 'caregiver' || user?.role === 'supervisor') && (
                            <>
                              {visit.status === 'scheduled' && (
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="rounded-xl gap-1"
                                  onClick={() => handleCheckIn(visit.id)}
                                  data-testid={`checkin-btn-${visit.id}`}
                                >
                                  <Play weight="bold" className="w-3 h-3" />
                                  Check In
                                </Button>
                              )}
                              {visit.status === 'in_progress' && (
                                <Button 
                                  size="sm" 
                                  className="rounded-xl gap-1"
                                  onClick={() => handleCheckOut(visit.id)}
                                  data-testid={`checkout-btn-${visit.id}`}
                                >
                                  <CheckCircle weight="bold" className="w-3 h-3" />
                                  Check Out
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit Visit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => { if (!open) { setIsEditOpen(false); setEditingVisit(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Visit</DialogTitle>
            <DialogDescription>Update visit details</DialogDescription>
          </DialogHeader>
          {editingVisit && (
            <form onSubmit={handleEditVisit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={editForm.client_id} onValueChange={(v) => setEditForm({ ...editForm, client_id: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="edit-visit-client"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Caregiver</Label>
                <Select value={editForm.caregiver_id} onValueChange={(v) => setEditForm({ ...editForm, caregiver_id: v })}>
                  <SelectTrigger className="rounded-xl" data-testid="edit-visit-caregiver"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {caregivers.map(cg => (
                      <SelectItem key={cg.id} value={cg.id} disabled={cg.needs_login}>
                        {cg.name}
                        {cg.is_supervisor && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-semibold">Supervisor</span>
                        )}
                        {cg.needs_login && (
                          <span className="ml-2 text-xs text-amber-600 font-medium">(needs login)</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input type="date" value={editForm.scheduled_date} onChange={(e) => setEditForm({ ...editForm, scheduled_date: e.target.value })} className="rounded-xl" data-testid="edit-visit-date" />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input type="time" value={editForm.scheduled_time} onChange={(e) => setEditForm({ ...editForm, scheduled_time: e.target.value })} className="rounded-xl" data-testid="edit-visit-time" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration</Label>
                  <Select value={String(editForm.duration_minutes)} onValueChange={(v) => setEditForm({ ...editForm, duration_minutes: parseInt(v) })}>
                    <SelectTrigger className="rounded-xl" data-testid="edit-visit-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 min</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="180">3 hours</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                      <SelectItem value="300">5 hours</SelectItem>
                      <SelectItem value="360">6 hours</SelectItem>
                      <SelectItem value="420">7 hours</SelectItem>
                      <SelectItem value="480">8 hours</SelectItem>
                      <SelectItem value="540">9 hours</SelectItem>
                      <SelectItem value="600">10 hours</SelectItem>
                      <SelectItem value="660">11 hours</SelectItem>
                      <SelectItem value="720">12 hours</SelectItem>
                      <SelectItem value="780">13 hours</SelectItem>
                      <SelectItem value="840">14 hours</SelectItem>
                      <SelectItem value="900">15 hours</SelectItem>
                      <SelectItem value="960">16 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                    <SelectTrigger className="rounded-xl" data-testid="edit-visit-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Notes..." className="rounded-xl" data-testid="edit-visit-notes" />
              </div>

              {/* Clock In/Out Times */}
              <div className="rounded-xl border border-border p-4 space-y-3 bg-muted/20">
                <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground" data-testid="edit-visit-tz-label">
                  Clock In / Out Times
                  <span className="ml-2 text-[10px] normal-case tracking-normal text-primary font-mono">
                    ({tzAbbrev(undefined, clients.find(c => c.id === editForm.client_id)?.timezone)} — client's local time)
                  </span>
                </Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Clock In</Label>
                    <Input
                      type="datetime-local"
                      value={editForm.check_in_time}
                      onChange={(e) => setEditForm({ ...editForm, check_in_time: e.target.value })}
                      className="rounded-xl text-sm"
                      data-testid="edit-visit-checkin"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Clock Out</Label>
                    <Input
                      type="datetime-local"
                      value={editForm.check_out_time}
                      onChange={(e) => setEditForm({ ...editForm, check_out_time: e.target.value })}
                      className="rounded-xl text-sm"
                      data-testid="edit-visit-checkout"
                    />
                  </div>
                </div>
                {editForm.check_in_time && editForm.check_out_time && (
                  <p className="text-xs text-muted-foreground">
                    Duration: {((new Date(editForm.check_out_time) - new Date(editForm.check_in_time)) / 3600000).toFixed(2)} hours
                  </p>
                )}
                <div className="space-y-1 pt-3 border-t border-border" data-testid="edit-visit-rate-override-block">
                  <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Billing Rate Override</Label>
                  <Select
                    value={editForm.billing_rate_override}
                    onValueChange={(v) => setEditForm({ ...editForm, billing_rate_override: v })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="edit-visit-rate-override-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto-detect (default)</SelectItem>
                      <SelectItem value="standard">Force Standard rate</SelectItem>
                      <SelectItem value="weekend">Force Weekend rate</SelectItem>
                      <SelectItem value="holiday">Force Holiday rate</SelectItem>
                      <SelectItem value="overtime">Force Overtime rate</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-muted-foreground">
                    Use this when auto-detection misses a special day (e.g., a holiday not on the office's list). Both client billing AND caregiver pay will use the selected rate.
                  </p>
                </div>

                {/* Per-visit Caregiver Pay Override + Bonus */}
                <div className="space-y-3 pt-3 border-t border-border" data-testid="edit-visit-pay-override-block">
                  <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Caregiver Pay (this visit only)</Label>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded"
                      checked={editForm.pay_rate_override_enabled}
                      onChange={(e) => setEditForm({ ...editForm, pay_rate_override_enabled: e.target.checked, pay_rate_override: e.target.checked ? editForm.pay_rate_override : '' })}
                      data-testid="edit-visit-pay-override-toggle"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Override caregiver hourly rate</p>
                      <p className="text-xs text-muted-foreground">Off = use caregiver's default rate + OT rules. On = flat $/hr for just this visit (bypasses OT/weekend/holiday).</p>
                    </div>
                  </label>
                  {editForm.pay_rate_override_enabled && (
                    <div className="pl-7 space-y-1.5">
                      <Label className="text-xs">Custom rate ($/hr)</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.pay_rate_override}
                          onChange={(e) => setEditForm({ ...editForm, pay_rate_override: e.target.value })}
                          placeholder="25.00"
                          className="rounded-xl pl-6"
                          data-testid="edit-visit-pay-override-amount"
                        />
                      </div>
                    </div>
                  )}
                  <div className="border-t border-border pt-3 space-y-1.5">
                    <Label className="text-xs">Bonus (paid to caregiver, NOT billed to client)</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-2">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={editForm.bonus_amount}
                          onChange={(e) => setEditForm({ ...editForm, bonus_amount: e.target.value })}
                          placeholder="0.00"
                          className="rounded-xl pl-6"
                          data-testid="edit-visit-bonus-amount"
                        />
                      </div>
                      <Input
                        value={editForm.bonus_reason}
                        onChange={(e) => setEditForm({ ...editForm, bonus_reason: e.target.value })}
                        placeholder="Reason (e.g. covered short-notice shift)"
                        className="rounded-xl"
                        disabled={!editForm.bonus_amount || parseFloat(editForm.bonus_amount) <= 0}
                        data-testid="edit-visit-bonus-reason"
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground">Adds to caregiver payroll for this visit. Not included in client billing/invoicing.</p>
                  </div>
                </div>

                <div className="space-y-1 pt-2 border-t border-border">
                  <Label className="text-xs">Reason for Changes <span className="text-destructive">*</span></Label>
                  <Textarea
                    value={editForm.change_reason}
                    onChange={(e) => setEditForm({ ...editForm, change_reason: e.target.value })}
                    placeholder="Required when editing clock times, tasks, or the rate override — e.g. 'Memorial Day — mark as holiday rate'"
                    className="rounded-xl text-sm min-h-[60px]"
                    data-testid="edit-visit-change-reason"
                  />
                </div>
              </div>

              {/* Tasks Completed (only for visits that have a task list) */}
              {editForm.tasks_completed && editForm.tasks_completed.length > 0 && (
                <div className="rounded-xl border border-border p-4 space-y-3 bg-muted/20" data-testid="edit-visit-tasks-section">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                      Tasks Completed ({editForm.tasks_completed.filter(t => t.completed).length}/{editForm.tasks_completed.length})
                    </Label>
                    {tasksDirty && <span className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold">edited</span>}
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {editForm.tasks_completed.map((task, idx) => (
                      <div key={idx} className="flex items-start gap-2 rounded-lg border border-border bg-background p-2" data-testid={`edit-task-row-${idx}`}>
                        <input
                          type="checkbox"
                          checked={!!task.completed}
                          onChange={() => toggleTaskCompleted(idx)}
                          className="mt-1 rounded"
                          data-testid={`edit-task-checkbox-${idx}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${task.completed ? 'font-medium' : 'text-muted-foreground'}`}>
                            {task.name}
                            {task.from_care_plan && <span className="ml-2 text-[10px] uppercase tracking-wider text-primary">Care Plan</span>}
                            {task.required && <span className="ml-2 text-[10px] uppercase tracking-wider text-red-600 font-semibold">Required</span>}
                          </p>
                          <Input
                            value={task.notes || ''}
                            onChange={(e) => updateTaskNotes(idx, e.target.value)}
                            placeholder="Notes (optional)..."
                            className="h-7 text-xs rounded-lg mt-1"
                            data-testid={`edit-task-notes-${idx}`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" className="rounded-xl gap-2 text-destructive border-destructive hover:bg-destructive/10" onClick={() => setIsDeleteOpen(true)} data-testid="delete-visit-btn">
                  <Trash weight="bold" className="w-4 h-4" /> Delete
                </Button>
                <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => { setIsEditOpen(false); setEditingVisit(null); }}>Cancel</Button>
                <Button type="submit" className="flex-1 rounded-xl" data-testid="save-visit-edit-btn">Save Changes</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Visit Confirmation */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-destructive">Delete Visit</DialogTitle>
            <DialogDescription>
              {editingVisit && (
                <>This will permanently delete the visit on <strong>{editingVisit.scheduled_date} {editingVisit.scheduled_time}</strong> for <strong>{getClientName(editingVisit.client_id)}</strong>. This cannot be undone.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsDeleteOpen(false)} disabled={deleting}>Cancel</Button>
            <Button type="button" className="flex-1 rounded-xl bg-destructive hover:bg-destructive/90 gap-2" onClick={handleDeleteVisit} disabled={deleting} data-testid="confirm-delete-visit-btn">
              <Trash weight="bold" className="w-4 h-4" />
              {deleting ? 'Deleting...' : 'Delete Visit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SchedulePage;
